import { useEffect, useRef, useState } from 'react'
import DateField from './DateField'
import TimeField from './TimeField'
import './styles.css'

/**
 * Date + time pair — drop-in for `<input type="datetime-local">`.
 *
 * `value` is a 'YYYY-MM-DDTHH:mm' string and `onChange` receives
 * `{ target: { name, id, value } }`. Emits only once both halves are set,
 * matching the native control (which stays empty until the pair is complete).
 */
export default function DateTimeField({
  id,
  name,
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  className = 'field',
}) {
  const [vDate = '', vTime = ''] = String(value || '').split('T')
  /* Draft keeps a half-completed pair visible. Emitting only a complete pair
     (native datetime-local behaviour) means the parent's value stays '' after
     the first pick — without the draft, the picked half vanished and the
     fields looked unselectable. The parent's value wins once it's set. */
  const [draft, setDraft] = useState({ date: '', time: '' })
  const date = vDate || draft.date
  const time = vTime || draft.time

  /* Drop the draft when `value` changes to something we didn't emit — i.e. the
     parent reset the form. Without this a reset would leave stale halves on
     screen, since the draft outlives an externally cleared value. */
  const lastEmitted = useRef('')
  useEffect(() => {
    if ((value || '') !== lastEmitted.current) setDraft({ date: '', time: '' })
  }, [value])

  const emit = (nextDate, nextTime) => {
    const next = nextDate && nextTime ? `${nextDate}T${nextTime}` : ''
    lastEmitted.current = next
    setDraft({ date: nextDate, time: nextTime })
    onChange({ target: { name, id, value: next } })
  }

  return (
    <div className="dtf-row">
      <DateField
        id={id}
        value={date}
        onChange={(e) => emit(e.target.value, time)}
        min={min ? String(min).split('T')[0] : undefined}
        max={max ? String(max).split('T')[0] : undefined}
        required={required}
        disabled={disabled}
        className={className}
      />
      <TimeField
        value={time}
        onChange={(e) => emit(date, e.target.value)}
        required={required}
        disabled={disabled}
        className={className}
      />
    </div>
  )
}
